// Withdrawal.js with Stripe Integration
import DataTable from "../DataTable";
import { useAuth } from "../../contexts/AuthContext";
import { useEffect, useState } from "react";
import {
  getUserByID,
  getCurrentUserDetails,
  getWithdrawalRequests,
  updateWithdrawalRequestStatus,
  getCurrentUserWithdrawalRequests,
  getOwnerAvailableBalance,
} from "../utils";
import { useNavigate } from "react-router-dom";
import { Row, Col, Modal, Button, Form } from "react-bootstrap";
import { Alert } from "@mui/material";
import CurrencyInput from "react-currency-input-field";
import { getDatabase, ref, get, update } from "firebase/database";
import { FaMoneyCheckAlt } from "react-icons/fa";
import { FaStripe } from "react-icons/fa";

const OWNER_WITHDRAWAL_GUIDANCE_KEY = "ownerWithdrawalGuidanceSeen";

export default function Withdrawal() {
  const Navigate = useNavigate();
  const auth = useAuth();
  const [formattedPayments, setFormattedPayments] = useState([]);
  const [currentBalance, setCurrentBalance] = useState([]);
  const [userDetails, setuserDetails] = useState([]);
  const [totalPending, setTotalPending] = useState(0);
  const [isRequestWithdrawalModalOpen, setRequestWithdrawalModalOpen] =
    useState(false);
  const [showWithdrawalsPageGuidanceModal, setShowWithdrawalsPageGuidanceModal] =
    useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [withdrawalAmount, setWithdrawalAmount] = useState(0);
  const [selectedRowUserId, setSelectedRowUserId] = useState("");
  const [selectedRowAmount, setSelectedRowAmount] = useState("");
  const [selectedRowId, setSelectedRowId] = useState("");
  const [selectedRowName, setSelectedRowName] = useState("");
  const [selectedRowStatus, setSelectedRowStatus] = useState("");
  const [totalAmountRequested, setTotalAmountRequested] = useState(0);
  const serverUrl = process.env.REACT_APP_BASE_URL;

  const openRequestWithdrawalModal = () => {
    setRequestWithdrawalModalOpen(true);
    setWithdrawalAmount(selectedRowAmount);
  };

  const closeRequestWithdrawalModal = () => {
    setRequestWithdrawalModalOpen(false);
  };

  const closeWithdrawalsPageGuidanceModal = () => {
    localStorage.setItem(OWNER_WITHDRAWAL_GUIDANCE_KEY, "1");
    setShowWithdrawalsPageGuidanceModal(false);
  };

  const processWithdrawal = async () => {
    try {
      setSuccess("Processing withdrawal request...");

      // First, approve the withdrawal request in the database
      await updateWithdrawalRequestStatus(selectedRowId, "Pending");

      // Then, process it with Stripe
      await processWithStripe();

      setTimeout(() => {
        setRequestWithdrawalModalOpen(false);
        setSuccess("");
        setError("");
        // Refresh the withdrawals list
        window.location.reload();
      }, 3000);
    } catch (error) {
      setError(`Error processing withdrawal: ${error.message}`);
      console.error("Error processing withdrawal:", error);
    }
  };

  const processWithStripe = async () => {
    if (!auth.token) {
      throw new Error("Not authenticated");
    }

    const response = await fetch(`${serverUrl}/process-stripe-withdrawal`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${auth.token}`,
      },
      body: JSON.stringify({
        withdrawalId: selectedRowId,
      }),
    });

    const result = await response.json();

    if (result.success) {
      setSuccess("Withdrawal has been processed successfully via Stripe!");
      setSelectedRowStatus("Processed");
    } else {
      setError(`Stripe processing failed: ${result.error}`);
      // Keep it as Approved if Stripe processing fails, so it can be retried
      setSelectedRowStatus("Approved");
    }
  };

  const rejectWithdrawal = async () => {
    try {
      const database = getDatabase();
      const ownerMetaRef = ref(database, `ownerMeta/${selectedRowUserId}`);

      // Retrieve current values
      const currentPaymentsSnapshot = await get(ownerMetaRef);
      const currentPayments = currentPaymentsSnapshot.val();

      // Robustly parse the original withdrawal amount in dollars.
      // `selectedRowAmount` is the display-formatted value ("$1056") while
      // `withdrawalAmount` may be the raw user-edited value ("1056").
      // Strip anything that isn't a digit or decimal point so we always
      // recover the correct dollar value, then convert to cents.
      const rawAmountSource =
        (selectedRowAmount && String(selectedRowAmount)) ||
        String(withdrawalAmount || "0");
      const refundDollars = parseFloat(rawAmountSource.replace(/[^0-9.]/g, ""));
      const refundCents = Math.round((isFinite(refundDollars) ? refundDollars : 0) * 100);

      // Update currentAmount while keeping totalAmount same
      const updatedPayments = {
        currentAmount:
          parseInt(currentPayments.payments.currentAmount) + refundCents,
        totalAmount: currentPayments.payments.totalAmount,
      };

      // Use update instead of set to modify only the specified fields
      await update(ownerMetaRef, { payments: updatedPayments });

      setSuccess("Withdrawal request has been rejected.");
      setSelectedRowStatus("Disapproved");

      await updateWithdrawalRequestStatus(selectedRowId, "Disapproved");

      setTimeout(() => {
        setRequestWithdrawalModalOpen(false);
        setSuccess("");
      }, 1000);
    } catch (error) {
      console.error("Error rejecting withdrawal:", error);
      setError(`Error rejecting withdrawal: ${error.message}`);
    }
  };

  const handleRowClick = (row) => {
    if (row && row.original && row.original.userId) {
      const userId = row.original.userId;
      const amount = row.original.amount;
      const id = row.original.id;
      const name = row.original.name;
      const status = row.original.status;

      setSelectedRowUserId(userId);
      setSelectedRowAmount(amount);
      setSelectedRowId(id);
      setSelectedRowName(name);
      setSelectedRowStatus(status);
      openRequestWithdrawalModal();
    } else {
      console.error("Invalid row object or missing data");
    }
  };

  const handleRowClickOwner = (row) => {
    // No action needed when owner clicks on a row
    return;
  };

  const handleWithdrawalRequest = async () => {
    try {
      // Form value is dollars; balance and local checks use cents.
      // Server expects dollars (withdrawals are stored in dollars).
      const amountDollars = parseFloat(withdrawalAmount);
      const amountInCents = Math.round(amountDollars * 100);

      if (isNaN(amountInCents) || amountInCents <= 0) {
        setError("Please enter a valid amount");
        return;
      }

      if (amountInCents > currentBalance.currentAmount) {
        setError("Withdrawal amount exceeds your available balance");
        return;
      }

      setSuccess("Processing your request...");

      if (!auth.token) {
        setError("Not authenticated");
        return;
      }

      // Always use Stripe endpoint
      const endpoint = `${serverUrl}/request-stripe-withdrawal`;

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${auth.token}`,
        },
        body: JSON.stringify({
          userId: auth.currentUser.uid,
          amount: amountDollars,
        }),
      });

      const result = await response.json();
      if (result.success) {
        setSuccess("Your withdrawal request has been submitted.");

        // Update UI to reflect the new balance
        setCurrentBalance((prevBalance) => ({
          ...prevBalance,
          currentAmount: prevBalance.currentAmount - amountInCents,
        }));

        // Reset form
        setWithdrawalAmount(0);

        // Close modal after a delay
        setTimeout(() => {
          setRequestWithdrawalModalOpen(false);
          setSuccess("");
          // Refresh the withdrawals list
          window.location.reload();
        }, 2000);
      } else {
        setError(result.error || "Failed to submit withdrawal request");
      }
    } catch (error) {
      console.error("Error submitting withdrawal request:", error);
      setError("An error occurred while processing your request");
    }
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userData = await getCurrentUserDetails();
        setuserDetails(userData);

        if (
          userData.role === "Boat Owner" &&
          !localStorage.getItem(OWNER_WITHDRAWAL_GUIDANCE_KEY)
        ) {
          setShowWithdrawalsPageGuidanceModal(true);
        }

        let paymentDetails = [];

        if (userData.role === "Admin") {
          paymentDetails = await getWithdrawalRequests();
        } else if (userData.role === "Boat Owner") {
          paymentDetails = await getCurrentUserWithdrawalRequests(
            auth.currentUser.uid
          );

          // Get current balance (reconciles ownerMeta with payment records + referral rate)
          const balance = await getOwnerAvailableBalance(auth.currentUser.uid);
          setCurrentBalance({
            currentAmount: balance.currentAmount,
            totalAmount: balance.totalEarnings,
          });
        }

        setTotalAmountRequested(0);
        setTotalPending(0);

        const formattedData = await Promise.all(
          paymentDetails.map(async (payment) => {
            const userDetails = await getUserByID(payment.userId);
            
            // Skip withdrawals from deleted users
            if (!userDetails || userDetails.isDeleted) {
              return null;
            }

            const timestamp = payment.timestamp;
            const date = new Date(timestamp);

            if (payment.status === "Pending") {
              setTotalPending((preValue) => preValue + 1);
              setTotalAmountRequested(
                (prevAmount) => parseInt(prevAmount) + parseInt(payment.amount)
              );
            }

            // Get various components of the date
            const year = date.getFullYear();
            const month = date.getMonth() + 1; // Months are zero-based
            const day = date.getDate();
            const hours = date.getHours();
            const minutes = date.getMinutes();

            // Create a formatted date string
            const formattedDate = `${year}-${month}-${day} ${hours}:${minutes}`;

            return {
              id: payment.id,
              userId: payment.userId,
              name: userDetails.firstName + " " + userDetails.lastName,
              amount: "$" + payment.amount,
              status: payment.status,
              paymentMethod: "Stripe", // Always use Stripe
              timestamp,
              time: formattedDate,
            };
          })
        );

        setFormattedPayments(formattedData.filter(Boolean));
      } catch (error) {
        console.error("Error fetching payment details:", error);
      }
    };

    fetchUserData();
  }, [auth.currentUser.uid]);

  const columnsAdmin = [
    { Header: "ID", accessor: "id" },
    { Header: "User ID", accessor: "userId" },
    { Header: "Name", accessor: "name" },
    { Header: "Amount", accessor: "amount" },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ value }) => {
        let className = "";
        if (value === "Pending") className = "text-warning";
        if (value === "Approved") className = "text-primary";
        if (value === "Processed") className = "text-success";
        if (value === "Disapproved") className = "text-danger";

        return <span className={className}>{value}</span>;
      },
    },
    {
      Header: "Payment Method",
      accessor: "paymentMethod",
      Cell: () => (
        <span>
          <FaStripe className="me-1" /> Stripe
        </span>
      ),
    },
    {
      Header: "Time",
      accessor: "timestamp",
      Cell: ({ row }) => row.original.time,
      sortType: (rowA, rowB, id) => rowA.values[id] - rowB.values[id],
    },
  ];

  const columnsOwner = [
    { Header: "ID", accessor: "id" },
    { Header: "Amount", accessor: "amount" },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ value }) => {
        let className = "";
        if (value === "Pending") className = "text-warning";
        if (value === "Approved") className = "text-primary";
        if (value === "Processed") className = "text-success";
        if (value === "Disapproved") className = "text-danger";

        return <span className={className}>{value}</span>;
      },
    },
    {
      Header: "Payment Method",
      accessor: "paymentMethod",
      Cell: () => (
        <span>
          <FaStripe className="me-1" /> Stripe
        </span>
      ),
    },
    {
      Header: "Time",
      accessor: "timestamp",
      Cell: ({ row }) => row.original.time,
      sortType: (rowA, rowB, id) => rowA.values[id] - rowB.values[id],
    },
  ];

  const defaultSort = { sortBy: [{ id: "timestamp", desc: true }] };

  return (
    <>
      {userDetails?.role === "Boat Owner" && (
        <Modal
          centered
          show={showWithdrawalsPageGuidanceModal}
          onHide={closeWithdrawalsPageGuidanceModal}
        >
          <Modal.Header closeButton>
            <Modal.Title>Your payout hub</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="mb-0">
              This page tracks your withdrawal requests. To cash out, go to
              Payments and hit Request Withdraw — just make sure Stripe is
              connected in Settings first. Payouts usually take 2–10 business
              days once approved.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeWithdrawalsPageGuidanceModal}>
              Got it
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                closeWithdrawalsPageGuidanceModal();
                Navigate("/dashboard/payments");
              }}
            >
              Go to payments
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      <div className="row">
        <div className="col">
          <div className="page-description">
            <h1>Withdrawals</h1>
          </div>
        </div>
      </div>

      {userDetails && userDetails.role === "Admin" ? (
        <Row>
          <Col xs={12} md={6}>
            <div className="card widget widget-bank-card">
              <div className="card-body">
                <div className="widget-bank-card-container widget-bank-card-mastercard d-flex flex-column">
                  <div className="card-logo">
                    <FaMoneyCheckAlt
                      style={{ width: "50px", height: "50px" }}
                    />
                  </div>
                  <span className="widget-bank-card-balance-title">
                    Withdraw Requests
                  </span>
                  <span className="widget-bank-card-balance">
                    {totalPending} pending
                  </span>
                </div>
              </div>
            </div>
          </Col>
          <Col xs={12} md={6}>
            <div className="card widget widget-bank-card">
              <div className="card-body">
                <div className="widget-bank-card-container widget-bank-card-visa d-flex flex-column">
                  <div className="card-logo">
                    <FaMoneyCheckAlt
                      style={{ width: "50px", height: "50px", color: "white" }}
                    />
                  </div>
                  <span className="widget-bank-card-balance-title">
                    TOTAL AMOUNT REQUESTED
                  </span>
                  <span className="widget-bank-card-balance">
                    ${totalAmountRequested}
                  </span>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      ) : null}

      <div className="row">
        <div className="col">
          <div className="card calendar-container">
            <div className="card-body">
              {userDetails.role === "Admin" && (
                <DataTable
                  columns={columnsAdmin}
                  data={formattedPayments ? formattedPayments : ""}
                  onRowClick={handleRowClick}
                  initialState={defaultSort}
                />
              )}
              {userDetails.role === "Boat Owner" && (
                <DataTable
                  columns={columnsOwner}
                  data={formattedPayments ? formattedPayments : ""}
                  onRowClick={handleRowClickOwner}
                  initialState={defaultSort}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Admin Modal */}
      {userDetails && userDetails.role === "Admin" && (
        <Modal
          centered
          show={isRequestWithdrawalModalOpen}
          onHide={closeRequestWithdrawalModal}
        >
          <Modal.Header closeButton>
            <Modal.Title>Process Withdrawal Request</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Alert severity="info">
              You'll transfer the funds via Stripe Connect.
            </Alert>
            <Form.Label className="mt-3">Boat Owner</Form.Label>
            <p>{selectedRowName || ""}</p>

            <Form.Group controlId="priceInput">
              <Form.Label className="mt-3">Amount</Form.Label>
              <CurrencyInput
                placeholder="Enter Price"
                defaultValue={
                  selectedRowAmount
                    ? parseFloat(selectedRowAmount.substring(1))
                    : 0
                }
                decimalsLimit={2}
                prefix="$"
                onValueChange={(value) => {
                  // Set maximum allowed value
                  const maxAllowedValue = selectedRowAmount
                    ? parseFloat(selectedRowAmount.substring(1))
                    : 4000;

                  // Check if the entered value is within the allowed range
                  if (value !== "") {
                    const numericValue = parseFloat(value);
                    if (
                      !isNaN(numericValue) &&
                      numericValue <= maxAllowedValue
                    ) {
                      setWithdrawalAmount(value);
                      setError("");
                    } else if (
                      !isNaN(numericValue) &&
                      numericValue > maxAllowedValue
                    ) {
                      setError(
                        "Amount cannot exceed the requested withdrawal amount"
                      );
                    } else {
                      setError("");
                      setWithdrawalAmount(0);
                    }
                  } else {
                    setWithdrawalAmount(0);
                    setError("");
                  }
                }}
                className="form-control"
              />

              {selectedRowStatus && selectedRowStatus !== "Pending" && (
                <h6 className="mt-3" style={{ textAlign: "center" }}>
                  Withdrawal requested {selectedRowAmount || ""}
                </h6>
              )}

              {error && (
                <Alert className="mt-3" severity="error">
                  {error}
                </Alert>
              )}
              {success && (
                <Alert className="mt-3" severity="success">
                  {success}
                </Alert>
              )}

              {selectedRowStatus && selectedRowStatus !== "Pending" && (
                <Alert className="mt-3" severity="success">
                  Withdrawal request has been {selectedRowStatus.toLowerCase()}
                </Alert>
              )}
            </Form.Group>
          </Modal.Body>

          <Modal.Footer>
            {selectedRowStatus && selectedRowStatus === "Pending" ? (
              <>
                <Button variant="danger" onClick={rejectWithdrawal}>
                  Disapprove
                </Button>
                <Button variant="primary" onClick={processWithdrawal}>
                  Approve & Process with Stripe
                </Button>
                <Button
                  variant="outline-primary"
                  onClick={() =>
                    updateWithdrawalRequestStatus(selectedRowId, "Approved")
                  }
                >
                  Approve Only (Manual Payment)
                </Button>
              </>
            ) : (
              selectedRowStatus === "Approved" && (
                <Button variant="primary" onClick={processWithdrawal}>
                  Process with Stripe
                </Button>
              )
            )}
          </Modal.Footer>
        </Modal>
      )}

      {/* Boat Owner Modal */}
      {userDetails && userDetails.role === "Boat Owner" && (
        <Modal
          centered
          show={isRequestWithdrawalModalOpen}
          onHide={closeRequestWithdrawalModal}
        >
          <Modal.Header closeButton>
            <Modal.Title>Request Withdrawal</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Alert severity="info">
              Withdrawal requests may take several days to process. Funds will
              be sent to your Stripe account.
            </Alert>

            <Form.Group controlId="priceInput">
              <Form.Label>Amount to withdraw</Form.Label>
              <CurrencyInput
                placeholder="Enter Amount"
                defaultValue={0}
                decimalsLimit={2}
                prefix="$"
                onValueChange={(value) => {
                  // Set maximum allowed value as current balance
                  const maxAllowedValue = currentBalance
                    ? currentBalance.currentAmount / 100
                    : 0;

                  if (value !== "") {
                    const numericValue = parseFloat(value);
                    if (
                      !isNaN(numericValue) &&
                      numericValue <= maxAllowedValue
                    ) {
                      setWithdrawalAmount(value);
                      setError("");
                    } else if (
                      !isNaN(numericValue) &&
                      numericValue > maxAllowedValue
                    ) {
                      setError("Exceeded current balance");
                    } else {
                      setError("");
                      setWithdrawalAmount(0);
                    }
                  } else {
                    setWithdrawalAmount(0);
                    setError("");
                  }
                }}
                className="form-control"
              />

              {error && (
                <Alert className="mt-3" severity="error">
                  {error}
                </Alert>
              )}

              <h6 className="mt-3" style={{ textAlign: "center" }}>
                Balance after withdrawal $
                {currentBalance
                  ? (
                      currentBalance.currentAmount / 100 -
                      (withdrawalAmount > 0 ? parseFloat(withdrawalAmount) : 0)
                    ).toFixed(2)
                  : "0.00"}
              </h6>

              {success && (
                <Alert className="mt-3" severity="success">
                  {success}
                </Alert>
              )}
            </Form.Group>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={closeRequestWithdrawalModal}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={withdrawalAmount <= 0 || error}
              onClick={handleWithdrawalRequest}
            >
              Submit
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
}
